import { Button } from '@mui/material';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';
import { convertToMexicoTime } from 'utils/helpers/convertToMexicoTime';

export const useTransactionFailed = ({
  failedTransactionsListIpbAction,
  failedTransactionsListIpb,
  cleanFailedTransactionsListClean,
  failedTransactionsDetailListIpbAction,
  failedTransactionsListDetailsIpb,
  cleanFailedTransactionsListIpbAction,
  transactionId,
  onClose,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { search } = location;
  const queryParams = new URLSearchParams(search);

  const { payload } = failedTransactionsListIpb || {};
  const { response } = failedTransactionsListDetailsIpb || {};
  const { rows, pagination } = payload || {};

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm();

  const [loading, setLoading] = useState(false);
  const [searchParams, setSearchParams] = useState({});
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(50);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [dialogMessage, setDialogMessage] = useState(false);
  const [isDialogOpenDetails, setIsDialogOpenDetails] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [action, setAction] = useState(null);
  const [error, setError] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [pendingSubmit, setPendingSubmit] = useState(false);

  // Sincroniza estados con la URL
  useEffect(() => {
    cleanFailedTransactionsListClean();

    const initialFilters = Object.fromEntries(queryParams.entries());
    setSearchParams(initialFilters);

    setPage(queryParams.get('page') ? parseInt(queryParams.get('page')) : 0);
    setSize(queryParams.get('size') ? parseInt(queryParams.get('size')) : 50);

    Object.keys(initialFilters).forEach((key) => {
      setValue(key, initialFilters[key]);
    });

    if (
      initialFilters.from_date > moment().format('YYYY-MM-DD') ||
      initialFilters.to_date > moment().format('YYYY-MM-DD')
    ) {
      setErrorMessage('No puedes ingresar fechas futuras');
      return;
    }

    if (initialFilters.from_date > initialFilters.to_date) {
      setErrorMessage('La fecha de inicio no puede ser mayor a la fecha final');
      return;
    }

    // Si hay un submit pendiente, activa la consulta
    if (pendingSubmit) {
      setIsSubmitted(true);
      setPendingSubmit(false);
    }
    // Elimina: setIsSubmitted(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  // Llama a la API solo cuando el usuario hace submit
  useEffect(() => {
    if (!isSubmitted) {
      setLoading(false);
      return;
    }

    const filteredSearchParams = Object.fromEntries(
      Object.entries(searchParams).filter(([, value]) => value !== undefined && value !== ''),
    );

    setLoading(true);
    failedTransactionsListIpbAction(page, size, filteredSearchParams)
      .then(() => {
        setLoading(false);
        setIsSubmitted(false);
      })
      .catch(() => {
        console.error('failedTransactionsListIpbAction error:', error);
        setLoading(false);
        setIsSubmitted(false);
      });
  }, [searchParams, page, size, failedTransactionsListIpbAction, error, isSubmitted]);

  useEffect(() => {
    if (transactionId) {
      setIsDialogOpenDetails(true);
      failedTransactionsDetailListIpbAction(transactionId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transactionId]);

  useEffect(() => {
    if (response?.response?.status > 200) {
      setDialogMessage('Error');
      setDialogMessage(response?.response?.data?.detail);
      setIsDialogOpen(true);
      setLoading(false);
    }
  }, [response]);

  useEffect(() => {
    if (failedTransactionsListIpb?.status === 200) {
      setLoading(false);
    } else if (failedTransactionsListIpb?.payload?.response?.status > 200) {
      setDialogMessage(failedTransactionsListIpb?.payload?.response?.data?.detail);
      setIsDialogOpen(true);
      setLoading(false);
    }
  }, [failedTransactionsListIpb]);

  const onSubmit = (data) => {
    const { from_date, to_date, icc_number } = data;
    if (!((from_date && to_date) || (icc_number && icc_number.trim() !== ''))) {
      setErrorMessage(
        'Debes seleccionar un rango de fechas (Desde y Hasta) o ingresar un ICCID para realizar la búsqueda.',
      );
      setLoading(false);
      return;
    }

    const filteredData = Object.fromEntries(
      Object.entries(data).filter(([, value]) => value !== undefined && value !== ''),
    );

    const { from_date: startDate, to_date: endDate } = filteredData;
    if (startDate && endDate && new Date(startDate) > new Date(endDate)) {
      setErrorMessage('La fecha de inicio no puede ser mayor a la fecha final');
      setLoading(false);
      return;
    }

    if (
      (startDate && startDate > moment().format('YYYY-MM-DD')) ||
      (endDate && endDate > moment().format('YYYY-MM-DD'))
    ) {
      setErrorMessage('No puedes ingresar fechas futuras');
      setLoading(false);
      return;
    }

    const queryString = new URLSearchParams(filteredData).toString();
    const currentQueryString = new URLSearchParams(searchParams).toString();

    setErrorMessage('');

    if (queryString === currentQueryString) {
      // Si los filtros son iguales, activa la consulta directamente
      setLoading(true);
      setIsSubmitted(true);
    } else {
      // Si los filtros cambian, navega y el efecto hará la consulta
      navigate(`/list/failed_transactions_ipb?${queryString}`);
      setLoading(true);
      setPendingSubmit(true);
    }
  };

  // Solo navega, no uses setPage ni setSize aquí
  const handleChangePage = (newPage) => {
    // Limpia datos al cambiar de página
    cleanFailedTransactionsListClean();

    const queryString = new URLSearchParams({
      ...searchParams,
      page: newPage,
    }).toString();
    navigate(`/list/failed_transactions_ipb?${queryString}`);
  };

  const handleChangePageSize = (newSize) => {
    // Limpia datos al cambiar tamaño de página
    cleanFailedTransactionsListClean();

    const queryString = new URLSearchParams({
      ...searchParams,
      size: newSize,
      page: 0,
    }).toString();
    navigate(`/list/failed_transactions_ipb?${queryString}`);
  };

  const mappedRows =
    rows
      ?.filter((row) => {
        const from = searchParams.from_date;
        const to = searchParams.to_date;
        const icc = searchParams.icc_number;
        let valid = true;

        // Limpia ambos valores para evitar problemas de espacios o ceros a la izquierda
        const rowIccStr = row.icc_number !== undefined ? String(row.icc_number).trim() : '';
        const filtroIccStr = icc !== undefined ? String(icc).trim() : '';

        if (icc) {
          console.log('Comparando ICCID:', {
            rowIcc: rowIccStr,
            filtroIcc: filtroIccStr,
            comparacion: rowIccStr === filtroIccStr,
          });
        }

        if (from && to) {
          const created = moment(row.created).format('YYYY-MM-DD');
          valid = valid && created >= from && created <= to;
        } else if (from) {
          const created = moment(row.created).format('YYYY-MM-DD');
          valid = valid && created >= from;
        } else if (to) {
          const created = moment(row.created).format('YYYY-MM-DD');
          valid = valid && created <= to;
        }

        // Filtro exacto por ICCID si existe (comparando como string y limpiando)
        if (icc) {
          valid = valid && rowIccStr === filtroIccStr;
        }

        return valid;
      })
      .map((row, idx) => ({
        ...row,
        id: row.icc_number ?? `${row.phone_number}_${row.icc_number}_${idx}`,
      })) || [];

  const handleIdButtonClick = (icc_number) => {
    if (icc_number) {
      setIsDialogOpenDetails(true);
      failedTransactionsDetailListIpbAction(icc_number)
        .then(() => setLoading(false))
        .catch(() => {
          setDialogMessage('Error');
          setDialogMessage('Error al cargar los detalles de la transferencia.');
          setIsDialogOpen(true);
          setLoading(false);
        });

      return () => {
        cleanFailedTransactionsListIpbAction();
      };
    }
  };

  const handleClose = () => {
    setIsDialogOpen(false);
    setIsDialogOpenDetails(false);
    // cleanFailedTransactionsListIpbAction();
    if (transactionId) {
      onClose();
    }
  };

  const handleOpenPopUp = () => {
    setAction(action);
  };

  const Columns = [
    {
      field: 'id',
      headerName: 'ID',
      width: 170,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Button variant='outlined' onClick={() => handleIdButtonClick(params.row.icc_number)}>
          {'Ver detalle'}
        </Button>
      ),
    },

    {
      field: 'created',
      headerName: 'Fecha de Compra',
      width: 250,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => {
        return convertToMexicoTime(params.row.created);
      },
    },
    {
      field: 'phone_number',
      headerName: 'Número telefónico',
      width: 200,
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: 'icc_number',
      headerName: 'ICCID',
      width: 250,
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: 'status_name',
      headerName: 'Status',
      width: 200,
      align: 'center',
      headerAlign: 'center',
    },
  ];

  return {
    register,
    handleSubmit,
    errors,
    onSubmit,
    loading,
    page,
    size,
    handleChangePage,
    pagination: {
      ...pagination,
      handleChangePage,
      handleChangePageSize,
    },
    rows,
    mappedRows,
    Columns,
    isDialogOpen,
    setIsDialogOpen,
    dialogMessage,
    handleClose,
    isDialogOpenDetails,
    errorMessage,
    setErrorMessage,
    setError,
    error,
    handleOpenPopUp,
  };
};
